import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BriefcaseMedical, CalendarCheck, ChevronRight, FileText } from "lucide-react";

/**
 * La cassetta di primo soccorso, sulla scheda del mezzo.
 *
 * Poche righe: com'e' messa, quanti prodotti vanno reintegrati e quando tocca
 * il prossimo controllo. Il dettaglio prodotto per prodotto sta nella pagina
 * delle cassette, che e' dove si registra il controllo.
 */
const COLORE = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-900",
  in_scadenza: "border-amber-200 bg-amber-50 text-amber-900",
  da_reintegrare: "border-red-200 bg-red-50 text-red-900",
  scaduto: "border-red-200 bg-red-50 text-red-900",
  non_presente: "border-slate-300 bg-slate-100 text-slate-700",
  mai_controllata: "border-sky-200 bg-sky-50 text-sky-900",
  controllo_scaduto: "border-orange-200 bg-orange-50 text-orange-900",
};

function dataIt(iso) {
  if (!iso) return "";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
}

export default function CassettaDelMezzo({ eqId }) {
  const [c, setC] = useState(undefined);

  useEffect(() => {
    let vivo = true;
    api.get(`/equipment/${eqId}/cassetta`)
      .then(({ data }) => { if (vivo) setC(data); })
      .catch(() => { if (vivo) setC(null); });
    return () => { vivo = false; };
  }, [eqId]);

  // Il foglio della cassetta di questo mezzo: contenuto con le scadenze,
  // controlli fatti e righe da firmare. E' quello che sta nel portadocumenti,
  // e da qui si ristampa senza passare dalla pagina delle cassette.
  const scarica = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.get(`/cassette/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `SC-PR-08.3.2 Cassetta ${(c.nome || "").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella generazione del foglio"); }
  };

  // Finche' non si sa, e per i mezzi che una cassetta non la portano, non si
  // occupa spazio sulla scheda.
  if (c === undefined || c === null) return null;

  const pezzi = [];
  if (c.da_reintegrare) pezzi.push(`${c.da_reintegrare} da reintegrare`);
  if (c.scaduti) pezzi.push(`${c.scaduti} scaduti`);
  if (c.in_scadenza) pezzi.push(`${c.in_scadenza} in scadenza`);

  return (
    <Card className={`border-2 ${COLORE[c.stato] || COLORE.ok}`}
          data-testid="cassetta-del-mezzo">
      <CardContent className="p-4">
        <Link to="/cassette" className="flex items-center gap-3">
          <BriefcaseMedical className="w-5 h-5 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-xs uppercase tracking-widest opacity-75">
              Cassetta di primo soccorso
            </span>
            <span className="block font-semibold leading-tight">{c.stato_nome}</span>
            {pezzi.length > 0 && (
              <span className="block text-sm">{pezzi.join(" · ")}</span>
            )}
            {!c.non_presente && (
              <span className="block text-xs opacity-75 flex items-center gap-1 mt-0.5">
                <CalendarCheck className="w-3 h-3" />
                {c.prossimo_controllo
                  ? `Prossimo controllo ${dataIt(c.prossimo_controllo)}`
                  : "Mai controllata"}
              </span>
            )}
          </span>
          <ChevronRight className="w-4 h-4 shrink-0 opacity-60" />
        </Link>
        <Button variant="outline" size="sm" onClick={scarica} data-testid="foglio-cassetta"
                className="mt-3 w-full bg-white/70"
                title="Il foglio della cassetta: contenuto, scadenze, controlli e righe da firmare">
          <FileText className="w-4 h-4 mr-2" /> Scarica il foglio della cassetta
        </Button>
      </CardContent>
    </Card>
  );
}
